import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Avatar,
  Button,
  Checkbox,
  EmptyState,
  Icon,
  IconButton,
  Input,
  Menu,
  Modal,
  PageHeader,
  Pill,
  Select,
  Skeleton,
  SkeletonText,
  Textarea,
  useToast,
} from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { DURATION_HINT, formatDuration, parseDuration, parseMentions, resolveMentions, stripMentions } from "@astrum/shared";
import type { TaskApproval as TaskApprovalData } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import type { ChecklistItem, CompletionMode, TaskAssignee, Tag } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { useRealtimeFrames } from "../lib/realtime-context.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { AttachmentList } from "../components/AttachmentList.js";
import { LinkedText } from "../components/LinkedText.js";
import { SaveTemplateDialog } from "../components/Lifecycle.js";
import { MentionAutocomplete } from "../components/MentionAutocomplete.js";
import { TaskChecklist } from "../components/TaskChecklist.js";
import { TaskTime } from "../components/TaskTime.js";
import { TaskApproval } from "../components/TaskApproval.js";
import { TaskCustomFields } from "../components/TaskCustomFields.js";
import { TaskStructureFields } from "../components/TaskStructureFields.js";
import "./task-detail.css";

/* ─────────────────────────────── local types ─────────────────────────────── */

/**
 * The full task-detail shape returned by GET /tasks/:taskId. Declared
 * independently of the shared `Task` type (which is a narrower list-view
 * shape) rather than extending it, so this file doesn't depend on fields
 * `Task` doesn't carry yet.
 */
type TaskDetailTask = {
  id: string;
  projectId: string | null;
  parentTaskId: string | null;
  blockedByTaskId: string | null;
  title: string;
  description: string;
  status: "todo" | "in_progress" | "review" | "done" | "cancelled";
  priority: "low" | "normal" | "medium" | "high" | "urgent";
  assigneeId: string | null;
  reporterId: string;
  dueAt: string | null;
  startAt: string | null;
  completedAt: string | null;
  estimateMinutes: number | null;
  position: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  assigneeName: string | null;
  assigneeAvatarUrl: string | null;
  assignees: TaskAssignee[];
  completionMode: CompletionMode;
  recurrenceRule: string | null;
  recurrenceParentId: string | null;
  reporterName: string | null;
  projectCode: string | null;
  projectName: string | null;
  subtaskCount: number;
  completedSubtaskCount: number;
  commentCount: number;
  sectionId?: string | null;
  milestoneId?: string | null;
  tags?: Tag[];
  customValues?: Record<string, string | number>;
};

type TaskDetailData = {
  task: TaskDetailTask;
  checklist?: ChecklistItem[];
  subtasks: TaskDetailTask[];
  comments: TaskComment[];
  /** Every task this one waits on, and every task waiting on this one (only those the viewer may read). */
  blockedBy: LinkedTask[];
  blocking: LinkedTask[];
  followers: Follower[];
  isFollowing: boolean;
  /** Admins and project managers may remove anyone's comment; everyone else only their own. */
  canModerateComments?: boolean;
  /** False for a read-only project viewer; absent on older servers (treated as editable). */
  canEdit?: boolean;
  /** Null when the task is not an approval task. */
  approval?: TaskApprovalData | null;
};

type LinkedTask = {
  id: string;
  title: string;
  status: string;
  dueAt: string | null;
  startAt: string | null;
  projectCode: string | null;
};

type Follower = { userId: string; displayName: string; avatarUrl: string | null };

type TaskComment = {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  createdAt: string;
  editedAt?: string | null;
  /** The thread keeps a deleted comment's place but not its words. */
  deleted?: boolean;
};

/** One line of a task's history, as GET /tasks/:id/activity returns it. */
type HistoryEntry = {
  id: string;
  kind: string;
  from: unknown;
  to: unknown;
  createdAt: string;
  actorId: string | null;
  actorName: string | null;
  actorAvatarUrl: string | null;
};

type ActivityFilter = "all" | "comments" | "history";

const isClosed = (status: string) => status === "done" || status === "cancelled";

/** A comment that has been optimistically appended but not yet confirmed. */
type PendingComment = TaskComment & { pending?: boolean; failed?: boolean };

const STATUS_TONE: Record<string, PillTone> = {
  todo: "neutral",
  in_progress: "accent",
  review: "info",
  done: "ok",
  cancelled: "neutral",
};

const PRIORITY_TONE: Record<string, PillTone> = {
  low: "neutral",
  normal: "neutral",
  medium: "warn",
  high: "danger",
  urgent: "danger",
};

const STATUS_LABEL: Record<string, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "In review",
  done: "Done",
  cancelled: "Cancelled",
};

/**
 * The rhythms worth offering, as the RRULEs they are stored as.
 *
 * Deliberately short. An RRULE can express "the last working day of every
 * quarter", but a dropdown that tries to cover the format's whole range stops
 * being a dropdown; anything rarer is left to a custom rule, which the control
 * preserves rather than overwrites.
 */
const RECURRENCE_PRESETS: { label: string; rule: string | null }[] = [
  { label: "Does not repeat", rule: null },
  { label: "Every day", rule: "FREQ=DAILY" },
  { label: "Every week", rule: "FREQ=WEEKLY" },
  { label: "Every two weeks", rule: "FREQ=WEEKLY;INTERVAL=2" },
  { label: "Every month", rule: "FREQ=MONTHLY" },
  { label: "Every three months", rule: "FREQ=MONTHLY;INTERVAL=3" },
  { label: "Every year", rule: "FREQ=YEARLY" },
];

/** Generates a client-side id so an optimistic comment can be reconciled with the server row. */
function newClientId(): string {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `cid-${Date.now()}-${Math.random()}`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDueDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Converts an ISO datetime to the value a `type="date"` input expects, in local time. */
function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

/*
 * Formatting moved to @astrum/shared (`formatDuration`).
 *
 * The version that lived here stopped at hours, so a week-long task read as
 * "40h" and a month as "160h" — technically true and useless. The shared one
 * carries days and weeks, and is the same code the parser round-trips against,
 * so what the field shows is always something it would accept back.
 */

/* ──────────────────────────────────── inline editable title ──────────────────────────────────── */

interface EditableTitleProps {
  value: string;
  onSave: (next: string) => void;
}

function EditableTitle({ value, onSave }: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else setDraft(value);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="task-detail__title-input"
        value={draft}
        aria-label="Task title"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <button type="button" className="task-detail__title-button" onClick={() => setEditing(true)}>
      {value}
      <Icon name="edit" size={14} className="task-detail__title-edit-icon" />
    </button>
  );
}

/* ──────────────────────────────────── subtask row ──────────────────────────────────── */

interface SubtaskRowProps {
  subtask: TaskDetailData["subtasks"][number];
  onToggle: (subtask: TaskDetailData["subtasks"][number]) => void;
  onOpen: (id: string) => void;
}

function SubtaskRow({ subtask, onToggle, onOpen }: SubtaskRowProps) {
  const isDone = subtask.status === "done";
  return (
    <div className="task-detail__subtask">
      <Checkbox
        checked={isDone}
        onChange={() => onToggle(subtask)}
        aria-label={isDone ? `Mark "${subtask.title}" as to do` : `Mark "${subtask.title}" as done`}
      />
      <button type="button" className={`task-detail__subtask-title ${isDone ? "is-done" : ""}`} onClick={() => onOpen(subtask.id)}>
        {subtask.title}
      </button>
      {subtask.assigneeName && <Avatar name={subtask.assigneeName} size={20} src={subtask.assigneeAvatarUrl ?? undefined} />}
    </div>
  );
}

/* ──────────────────────────────────── history lines ──────────────────────────────────── */

const shortDate = (value: unknown) =>
  typeof value === "string" && value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "none";

/** The label inside a history value: a column or person `name`, a task `title`, or a file's `fileName`. */
const nameOf = (value: unknown) => {
  if (!value || typeof value !== "object") return "none";
  const v = value as Record<string, unknown>;
  return String(v.name ?? v.title ?? v.fileName ?? "none");
};

/** A history entry as a sentence fragment, following the actor's name. */
function describeActivity(entry: HistoryEntry): string {
  const { kind, from, to } = entry;
  switch (kind) {
    case "created":
      return "created this task";
    case "title":
      return `renamed the task to "${String(to)}"`;
    case "description":
      return "edited the description";
    case "status":
      return `changed the status from ${STATUS_LABEL[String(from)] ?? String(from)} to ${STATUS_LABEL[String(to)] ?? String(to)}`;
    case "column":
      return `moved it from ${nameOf(from)} to ${nameOf(to)}`;
    case "priority":
      return `changed the priority from ${String(from)} to ${String(to)}`;
    case "due":
      return to ? `set the due date to ${shortDate(to)}` : "cleared the due date";
    case "start":
      return to ? `set the start date to ${shortDate(to)}` : "cleared the start date";
    case "estimate":
      return to == null ? "cleared the estimate" : `set the estimate to ${formatDuration(Number(to))}`;
    case "parent":
      return to ? `made it a subtask of ${nameOf(to)}` : "made it a top-level task";
    case "recurrence":
      return to ? "made it repeat" : "stopped it repeating";
    case "assignees": {
      const names = Array.isArray(to) ? (to as { name: string }[]).map((a) => a.name) : [];
      return names.length ? `changed the owners to ${names.join(", ")}` : "removed all owners";
    }
    case "blocker.added":
      return `added the blocker "${nameOf(to)}"`;
    case "blocker.removed":
      return `removed the blocker "${nameOf(from)}"`;
    case "section":
      return to ? `moved it to the section ${nameOf(to)}` : "removed it from its section";
    case "milestone":
      return to ? `linked it to the milestone ${nameOf(to)}` : "unlinked it from its milestone";
    case "tags": {
      const names = Array.isArray(to) ? (to as string[]) : [];
      return names.length ? `set the tags to ${names.join(", ")}` : "removed all tags";
    }
    case "checklist.added":
      return `added the checklist item "${String((to as { text?: string } | null)?.text ?? "")}"`;
    case "checklist.checked":
      return `ticked "${String((to as { text?: string } | null)?.text ?? "")}"`;
    case "checklist.unchecked":
      return `unticked "${String((to as { text?: string } | null)?.text ?? "")}"`;
    case "checklist.removed":
      return `removed the checklist item "${String((from as { text?: string } | null)?.text ?? "")}"`;
    case "attachment.added":
      return `attached ${nameOf(to)}`;
    case "attachment.removed":
      return `removed the attachment ${nameOf(from)}`;
    case "automation":
      return `ran the automation "${String((to as { name?: string } | null)?.name ?? "")}"`;
    default:
      return kind;
  }
}

/** A comment body with its `@[Name](id)` mentions shown as chips. */
function CommentText({ body, viewerId }: { body: string; viewerId?: string }) {
  const mentions = parseMentions(body);
  if (mentions.length === 0) return <LinkedText text={body} />;
  const parts: ReactNode[] = [];
  let at = 0;
  mentions.forEach((mention, i) => {
    if (mention.start > at) parts.push(<LinkedText key={`t${i}`} text={body.slice(at, mention.start)} />);
    parts.push(
      <span key={`m${i}`} className={`task-detail__mention ${mention.userId === viewerId ? "is-you" : ""}`}>
        @{mention.label}
      </span>,
    );
    at = mention.end;
  });
  if (at < body.length) parts.push(<LinkedText key="tail" text={body.slice(at)} />);
  return <>{parts}</>;
}

/* ──────────────────────────────────── comment composer ──────────────────────────────────── */

type Person = { id: string; displayName: string; jobTitle: string | null };

interface MentionFieldProps {
  value: string;
  onChange: (value: string) => void;
  people: Person[];
  label: string;
  placeholder?: string;
  onSubmit: () => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

/** A textarea that offers people when `@` is typed. Enter submits unless the picker is open. */
function MentionField({ value, onChange, people, label, placeholder, onSubmit, onCancel, autoFocus }: MentionFieldProps) {
  const [caret, setCaret] = useState(value.length);
  const [pickerOpen, setPickerOpen] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // The picker owns Enter and the arrows while it is open.
    if (pickerOpen && ["Enter", "Tab", "ArrowUp", "ArrowDown", "Escape"].includes(event.key)) return;
    // Enter sends; Shift+Enter inserts a newline, matching Chat's composer.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    } else if (event.key === "Escape" && onCancel) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="task-detail__field-wrap">
      <MentionAutocomplete
        value={value}
        caret={caret}
        people={people}
        onOpenChange={setPickerOpen}
        onPick={(next, nextCaret) => {
          onChange(next);
          setCaret(nextCaret);
          requestAnimationFrame(() => {
            const node = ref.current;
            if (!node) return;
            node.focus();
            node.setSelectionRange(nextCaret, nextCaret);
          });
        }}
      />
      <Textarea
        ref={ref}
        aria-label={label}
        placeholder={placeholder}
        value={value}
        maxRows={8}
        autoFocus={autoFocus}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
        }}
        onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
        onKeyDown={handleKeyDown}
        className="task-detail__composer-field"
      />
    </div>
  );
}

interface CommentComposerProps {
  onSend: (body: string) => void;
  people: Person[];
}

function CommentComposer({ onSend, people }: CommentComposerProps) {
  const [value, setValue] = useState("");

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    // What is typed is readable `@Name`; the stored form records who was named.
    onSend(resolveMentions(trimmed, people));
    setValue("");
  }, [value, onSend, people]);

  return (
    <div className="task-detail__composer">
      <MentionField value={value} onChange={setValue} people={people} label="Add a comment" placeholder="Add a comment… use @ to mention" onSubmit={submit} />
      <div className="task-detail__composer-bar">
        <div className="task-detail__composer-spacer" />
        <IconButton icon={<Icon name="send" size={15} />} label="Post comment" variant="solid" onClick={submit} disabled={!value.trim()} />
      </div>
    </div>
  );
}

/* ──────────────────────────────────── blockers ──────────────────────────────────── */

interface BlockerEditorProps {
  task: TaskDetailTask;
  blockedBy: LinkedTask[];
  blocking: LinkedTask[];
  canEdit: boolean;
  onAdd: (blockerId: string) => void;
  onRemove: (blockerId: string) => void;
  onOpen: (taskId: string) => void;
}

/**
 * What this task waits on, and what waits on it.
 *
 * Adding is a search over every task the viewer can see (the server refuses
 * cycles and anything they cannot read). A blocker due after this task starts
 * or is due gets a warning, not a refusal: plans slip, and the point is to
 * make the clash visible.
 */
function BlockerEditor({ task, blockedBy, blocking, canEdit, onAdd, onRemove, onOpen }: BlockerEditorProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LinkedTask[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .get<{ tasks: LinkedTask[] }>(`/tasks?search=${encodeURIComponent(term)}&includeSubtasks=true&limit=8`)
        .then((res) => !cancelled && setResults(res.tasks))
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setSearching(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const existing = new Set([task.id, ...blockedBy.map((b) => b.id)]);
  const candidates = results.filter((r) => !existing.has(r.id));
  const openCount = blockedBy.filter((b) => !isClosed(b.status)).length;

  // A blocker that ends after this task is meant to start (or be finished) cannot be honoured.
  const clashes = (blocker: LinkedTask) => {
    if (!blocker.dueAt || isClosed(blocker.status)) return false;
    const due = new Date(blocker.dueAt).getTime();
    return (!!task.startAt && due > new Date(task.startAt).getTime()) || (!!task.dueAt && due > new Date(task.dueAt).getTime());
  };

  if (!canEdit && blockedBy.length === 0 && blocking.length === 0) return null;

  return (
    <div className="task-detail__section task-detail__blockers">
      <div className="task-detail__section-header">
        <h3 className="task-detail__section-title">Blocked by</h3>
        {blockedBy.length > 0 && (
          <span className="task-detail__subtask-progress">{openCount === 0 ? "all clear" : `${openCount} open`}</span>
        )}
      </div>

      {blockedBy.map((blocker) => {
        const open = !isClosed(blocker.status);
        return (
          <div key={blocker.id} className={`task-detail__blocker ${open ? "is-open" : ""}`}>
            <Icon name="warning" size={15} />
            <span className="task-detail__blocker-text">
              <button type="button" className="task-detail__blocker-link" onClick={() => onOpen(blocker.id)}>
                {blocker.title}
              </button>
              {clashes(blocker) && (
                <span className="task-detail__blocker-warn" role="status">
                  Due {formatDueDate(blocker.dueAt!)}, after this task {task.startAt && new Date(blocker.dueAt!) > new Date(task.startAt) ? "starts" : "is due"}
                </span>
              )}
            </span>
            <Pill tone={open ? "danger" : "ok"}>{STATUS_LABEL[blocker.status] ?? blocker.status}</Pill>
            {canEdit && (
              <IconButton
                icon={<Icon name="close" size={13} />}
                label={`Remove blocker ${blocker.title}`}
                size="sm"
                variant="ghost"
                onClick={() => onRemove(blocker.id)}
              />
            )}
          </div>
        );
      })}

      {canEdit && (
        <div className="task-detail__blocker-add">
          <Input
            aria-label="Search for a task that blocks this one"
            placeholder="Add a blocker — search tasks…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query.trim() && (
            <div className="task-detail__blocker-results" role="listbox" aria-label="Matching tasks">
              {candidates.length === 0 ? (
                <div className="task-detail__blocker-empty">{searching ? "Searching…" : "No matching tasks"}</div>
              ) : (
                candidates.map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="task-detail__blocker-option"
                    onClick={() => {
                      onAdd(candidate.id);
                      setQuery("");
                    }}
                  >
                    {candidate.projectCode && <Pill tone="neutral">{candidate.projectCode}</Pill>}
                    <span>{candidate.title}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {blocking.length > 0 && (
        <>
          <div className="task-detail__section-header">
            <h3 className="task-detail__section-title">Blocking</h3>
          </div>
          {blocking.map((other) => (
            <div key={other.id} className="task-detail__blocker">
              <Icon name="work" size={15} />
              <span className="task-detail__blocker-text">
                <button type="button" className="task-detail__blocker-link" onClick={() => onOpen(other.id)}>
                  {other.title}
                </button>
              </span>
              <Pill tone={STATUS_TONE[other.status] ?? "neutral"}>{STATUS_LABEL[other.status] ?? other.status}</Pill>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────── main view ──────────────────────────────────── */

export function TaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Whose checkbox is whose: only the viewer's own completion is editable.
  const auth = useAuth();

  const [data, setData] = useState<TaskDetailData | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  usePageTitle(data?.task.title ?? "");

  const [comments, setComments] = useState<PendingComment[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyTick, setHistoryTick] = useState(0);
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [followBusy, setFollowBusy] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** For the assignee control — a task created unassigned has to be assignable. */
  const [people, setPeople] = useState<{ id: string; displayName: string; jobTitle: string | null }[]>([]);
  /** Shown under the estimate field when what was typed is not a duration. */
  const [estimateError, setEstimateError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    if (!taskId) return () => undefined;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    api
      .get<TaskDetailData>(`/tasks/${taskId}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setComments(res.comments);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 404) {
          setNotFound(true);
        } else {
          toast(error instanceof ApiError ? error.message : "Could not load task.", { tone: "danger" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => load(), [load]);

  /*
   * Live comments from assignees/the reporter — the server publishes a
   * `task_comment` frame to each of them (see POST /tasks/:taskId/comments).
   * Filtered on authorId the same way Feed filters its own frames: this
   * viewer's own comment is already in `comments` via sendComment's
   * optimistic-then-confirmed flow below, so appending it again here would
   * double it.
   */
  useRealtimeFrames((frame) => {
    if (frame.t !== "task_comment") return;
    if (frame.comment.taskId !== taskId) return;
    if (frame.comment.authorId === auth.viewer?.id) return;
    setComments((prev) => {
      if (prev.some((c) => c.id === frame.comment.id)) return prev;
      const { taskId: _taskId, ...comment } = frame.comment;
      return [...prev, { ...comment, authorAvatarUrl: null }];
    });
  });

  // Fetched once: the roster does not change while a task is open, and the
  // control needs it whether or not the task currently has an assignee.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ people: { id: string; displayName: string; jobTitle: string | null }[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── keep the activity thread scrolled to the newest comment ── */
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [comments.length, filter]);

  /**
   * Patches the task, sending its current version for optimistic concurrency.
   * A 409 means someone else changed it first: the user's in-flight edit is
   * not silently discarded, but it's also not blindly retried — they're told
   * and the record is refetched so they can decide what to do with a fresh copy.
   */
  const patchTask = useCallback(
    (fields: Record<string, unknown>) => {
      if (!taskId || !data) return;
      const version = data.task.version;
      // Only the fields being changed are snapshotted, rather than the whole
      // payload: restoring all of `data` would also discard a comment or a
      // subtask edit that landed while this request was in flight.
      const previousTask = data.task;
      const revertFields = Object.fromEntries(
        Object.keys(fields).map((key) => [key, previousTask[key as keyof typeof previousTask]]),
      );

      // Optimistic local update so a Select/blur feels instant.
      setData((prev) => (prev ? { ...prev, task: { ...prev.task, ...fields } } : prev));

      api
        .patch<{ task: TaskDetailData["task"] }>(`/tasks/${taskId}`, { ...fields, version })
        .then((res) => {
          setData((prev) => (prev ? { ...prev, task: { ...prev.task, ...res.task } } : prev));
        })
        .catch((error: unknown) => {
          setData((prev) => (prev ? { ...prev, task: { ...prev.task, ...revertFields } } : prev));
          if (error instanceof ApiError && error.status === 409) {
            toast("Someone else changed this task. Reloading the latest version.", { tone: "danger" });
            load();
          } else {
            toast(error instanceof ApiError ? error.message : "Could not save that change.", { tone: "danger" });
          }
        });
    },
    [taskId, data, toast, load],
  );

  /**
   * Replace the owner set.
   *
   * Not routed through `patchTask`: ownership lives in its own table behind its
   * own endpoint, and the response can change the task's *status* as well as
   * its owners — removing the one person who had not finished completes an
   * "everyone" task. So the whole task is taken from the response rather than
   * merging the fields that were sent.
   */
  const setOwners = useCallback(
    (userIds: string[]) => {
      if (!taskId || !data) return;
      const previous = data.task;

      // Optimistic, but only for the people already known: a newly added owner
      // has no `completedAt` to guess at, and arrives hydrated on the response.
      setData((prev) =>
        prev
          ? {
              ...prev,
              task: {
                ...prev.task,
                assignees: prev.task.assignees.filter((a) => userIds.includes(a.userId)),
              },
            }
          : prev,
      );

      api
        .put<{ task: TaskDetailData["task"] }>(`/tasks/${taskId}/assignees`, { userIds })
        .then((res) => setData((prev) => (prev ? { ...prev, task: { ...prev.task, ...res.task } } : prev)))
        .catch((error: unknown) => {
          setData((prev) => (prev ? { ...prev, task: previous } : prev));
          toast(error instanceof ApiError ? error.message : "Could not change who owns this.", { tone: "danger" });
        });
    },
    [taskId, data, toast],
  );

  /**
   * Tick or untick your own part.
   *
   * Deliberately separate from the task's status control: "I have finished my
   * part" is a different statement from "this task is finished", and which one
   * implies the other is exactly what the completion rule decides. The server
   * settles that, so the response is taken wholesale rather than assuming the
   * status followed.
   */
  const setMyCompletion = useCallback(
    (completed: boolean) => {
      if (!taskId || !data || !auth.viewer) return;
      const previous = data.task;
      const at = completed ? new Date().toISOString() : null;

      setData((prev) =>
        prev
          ? {
              ...prev,
              task: {
                ...prev.task,
                assignees: prev.task.assignees.map((a) =>
                  a.userId === auth.viewer!.id ? { ...a, completedAt: at } : a,
                ),
              },
            }
          : prev,
      );

      api
        .put<{ task: TaskDetailData["task"] }>(`/tasks/${taskId}/assignees/me/completion`, { completed })
        .then((res) => {
          setData((prev) => (prev ? { ...prev, task: { ...prev.task, ...res.task } } : prev));
          // Finishing a repeating task creates the next one, which is a new row
          // this screen has no other way of learning about.
          if (res.task.status === "done" && previous.recurrenceRule) {
            toast("Done — the next one has been scheduled.");
          }
        })
        .catch((error: unknown) => {
          setData((prev) => (prev ? { ...prev, task: previous } : prev));
          toast(error instanceof ApiError ? error.message : "Could not save that.", { tone: "danger" });
        });
    },
    [taskId, data, auth.viewer, toast],
  );

  const toggleSubtask = useCallback(
    (subtask: TaskDetailData["subtasks"][number]) => {
      if (!data) return;
      const nextStatus = subtask.status === "done" ? "todo" : "done";
      const previousSubtasks = data.subtasks;
      const previousCompletedCount = data.task.completedSubtaskCount;

      setData((prev) =>
        prev
          ? {
              ...prev,
              subtasks: prev.subtasks.map((s) => (s.id === subtask.id ? { ...s, status: nextStatus } : s)),
              task: {
                ...prev.task,
                completedSubtaskCount: (prev.task.completedSubtaskCount ?? 0) + (nextStatus === "done" ? 1 : -1),
              },
            }
          : prev,
      );

      api
        .patch<{ task: TaskDetailData["task"] }>(`/tasks/${subtask.id}`, { status: nextStatus, version: subtask.version })
        .then((res) => {
          setData((prev) =>
            prev ? { ...prev, subtasks: prev.subtasks.map((s) => (s.id === subtask.id ? { ...s, ...res.task } : s)) } : prev,
          );
        })
        .catch((error: unknown) => {
          // The count is rolled back alongside the list — leaving it advanced
          // would show "2/3 done" over a list with one box ticked.
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  subtasks: previousSubtasks,
                  task: { ...prev.task, completedSubtaskCount: previousCompletedCount },
                }
              : prev,
          );
          if (error instanceof ApiError && error.status === 409) {
            toast("That subtask changed elsewhere. Reloading.", { tone: "danger" });
            load();
          } else {
            toast(error instanceof ApiError ? error.message : "Could not update the subtask.", { tone: "danger" });
          }
        });
    },
    [data, toast, load],
  );

  const addSubtask = useCallback(() => {
    const title = newSubtaskTitle.trim();
    if (!title || !taskId) return;
    setAddingSubtask(true);

    api
      .post<{ task: TaskDetailData["subtasks"][number] }>("/tasks", { title, parentTaskId: taskId })
      .then((res) => {
        setData((prev) =>
          prev
            ? {
                ...prev,
                subtasks: [...prev.subtasks, res.task],
                task: { ...prev.task, subtaskCount: (prev.task.subtaskCount ?? 0) + 1 },
              }
            : prev,
        );
        setNewSubtaskTitle("");
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not add subtask.", { tone: "danger" });
      })
      .finally(() => setAddingSubtask(false));
  }, [newSubtaskTitle, taskId, toast]);

  /*
   * The history half of the Activity panel. Comments already arrive with the
   * task (and live, over the socket); this fetches the other lines, newest
   * first, and is refetched whenever the task changes under the viewer.
   */
  const taskVersion = data?.task.version;
  useEffect(() => {
    if (!taskId) return undefined;
    let cancelled = false;
    api
      .get<{ activity: HistoryEntry[]; nextCursor: string | null }>(`/tasks/${taskId}/activity?filter=history&limit=30`)
      .then((res) => {
        if (cancelled) return;
        setHistory(res.activity);
        setHistoryCursor(res.nextCursor);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [taskId, taskVersion, historyTick]);

  const refreshHistory = useCallback(() => setHistoryTick((n) => n + 1), []);

  const loadEarlierHistory = useCallback(() => {
    if (!taskId || !historyCursor) return;
    api
      .get<{ activity: HistoryEntry[]; nextCursor: string | null }>(
        `/tasks/${taskId}/activity?filter=history&limit=30&before=${encodeURIComponent(historyCursor)}`,
      )
      .then((res) => {
        setHistory((prev) => [...prev, ...res.activity.filter((a) => !prev.some((p) => p.id === a.id))]);
        setHistoryCursor(res.nextCursor);
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not load earlier history.", { tone: "danger" }));
  }, [taskId, historyCursor, toast]);

  const toggleFollow = useCallback(() => {
    if (!taskId || !data) return;
    setFollowBusy(true);
    const request = data.isFollowing ? api.delete<{ followers: Follower[]; isFollowing: boolean }>(`/tasks/${taskId}/follow`) : api.put<{ followers: Follower[]; isFollowing: boolean }>(`/tasks/${taskId}/follow`, {});
    request
      .then((res) => setData((prev) => (prev ? { ...prev, followers: res.followers, isFollowing: res.isFollowing } : prev)))
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not change following.", { tone: "danger" }))
      .finally(() => setFollowBusy(false));
  }, [taskId, data, toast]);

  const saveCommentEdit = useCallback(() => {
    if (!taskId || !editingId) return;
    const trimmed = editValue.trim();
    if (!trimmed) return;
    const body = resolveMentions(trimmed, people);
    const id = editingId;
    api
      .patch<{ comment: TaskComment }>(`/tasks/${taskId}/comments/${id}`, { body })
      .then((res) => {
        setComments((prev) => prev.map((c) => (c.id === id ? { ...c, ...res.comment } : c)));
        setEditingId(null);
        refreshHistory();
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not edit the comment.", { tone: "danger" }));
  }, [taskId, editingId, editValue, people, toast, refreshHistory]);

  const deleteComment = useCallback(
    (id: string) => {
      if (!taskId) return;
      api
        .delete(`/tasks/${taskId}/comments/${id}`)
        .then(() => {
          setComments((prev) => prev.map((c) => (c.id === id ? { ...c, body: "", deleted: true } : c)));
          setData((prev) => (prev ? { ...prev, task: { ...prev.task, commentCount: Math.max(0, prev.task.commentCount - 1) } } : prev));
          refreshHistory();
        })
        .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not delete the comment.", { tone: "danger" }));
    },
    [taskId, toast, refreshHistory],
  );

  const addBlocker = useCallback(
    (blockedByTaskId: string) => {
      if (!taskId) return;
      api
        .post<{ blockedBy: LinkedTask[]; blocking: LinkedTask[] }>(`/tasks/${taskId}/blockers`, { blockedByTaskId })
        .then((res) => {
          setData((prev) => (prev ? { ...prev, blockedBy: res.blockedBy, blocking: res.blocking } : prev));
          refreshHistory();
        })
        .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not add the blocker.", { tone: "danger" }));
    },
    [taskId, toast, refreshHistory],
  );

  const removeBlocker = useCallback(
    (blockerId: string) => {
      if (!taskId) return;
      api
        .delete<{ blockedBy: LinkedTask[]; blocking: LinkedTask[] }>(`/tasks/${taskId}/blockers/${blockerId}`)
        .then((res) => {
          setData((prev) => (prev ? { ...prev, blockedBy: res.blockedBy, blocking: res.blocking } : prev));
          refreshHistory();
        })
        .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not remove the blocker.", { tone: "danger" }));
    },
    [taskId, toast, refreshHistory],
  );

  const sendComment = useCallback(
    (body: string) => {
      if (!taskId) return;
      const clientId = newClientId();
      const optimistic: PendingComment = {
        id: clientId,
        body,
        authorId: "me",
        authorName: "You",
        authorAvatarUrl: null,
        createdAt: new Date().toISOString(),
        pending: true,
      };
      setComments((prev) => [...prev, optimistic]);

      api
        .post<{ comment: TaskComment }>(`/tasks/${taskId}/comments`, { body })
        .then((res) => {
          setComments((prev) => prev.map((c) => (c.id === clientId ? { ...res.comment } : c)));
          setData((prev) => (prev ? { ...prev, task: { ...prev.task, commentCount: prev.task.commentCount + 1 } } : prev));
          refreshHistory();
        })
        .catch((error: unknown) => {
          setComments((prev) => prev.filter((c) => c.id !== clientId));
          toast(error instanceof ApiError ? error.message : "Comment failed to send.", { tone: "danger" });
        });
    },
    [taskId, toast, refreshHistory],
  );

  const handleDelete = useCallback(() => {
    if (!taskId) return;
    setDeleting(true);
    api
      .delete(`/tasks/${taskId}`)
      .then(() => {
        toast("Task deleted. Its subtasks were promoted to top-level tasks.", { tone: "ok" });
        navigate("/work");
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not delete task.", { tone: "danger" });
      })
      .finally(() => {
        setDeleting(false);
        setDeleteOpen(false);
      });
  }, [taskId, toast, navigate]);

  const subtaskProgress = useMemo(() => {
    if (!data) return null;
    const total = data.task.subtaskCount ?? data.subtasks.length;
    if (total === 0) return null;
    const done = data.task.completedSubtaskCount ?? data.subtasks.filter((s) => s.status === "done").length;
    return { done, total };
  }, [data]);

  const canEditTask = data?.canEdit !== false;

  /** Comments and history lines as one newest-last thread, narrowed by the filter. */
  const thread = useMemo(() => {
    const items: ({ type: "comment"; at: string; comment: PendingComment } | { type: "history"; at: string; entry: HistoryEntry })[] = [];
    if (filter !== "history") for (const comment of comments) items.push({ type: "comment", at: comment.createdAt, comment });
    if (filter !== "comments") for (const entry of history) items.push({ type: "history", at: entry.createdAt, entry });
    return items.sort((a, b) => a.at.localeCompare(b.at));
  }, [comments, history, filter]);

  if (notFound) {
    return (
      <div className="task-detail__empty-wrap">
        <EmptyState
          icon="work"
          title="Task not found"
          description="It may have been deleted, or you may not have access to it."
          action={
            <Button variant="secondary" onClick={() => navigate("/work")}>
              Back to Work
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="task-detail">
      <PageHeader
        className="task-detail__header"
        /* The project code is a link back to its board, so it belongs beside
           the back button rather than trailing the title. */
        leading={
          <>
            <button type="button" className="task-detail__back" onClick={() => navigate("/work")} aria-label="Back to Work">
              <Icon name="chevron-left" size={18} />
            </button>
            {data?.task.projectCode && (
              <button type="button" className="task-detail__project-pill" onClick={() => navigate(`/work?project=${data.task.projectId}`)}>
                <Pill tone="neutral">{data.task.projectCode}</Pill>
              </button>
            )}
          </>
        }
        /* While loading the heading is a placeholder bar, so the bar does not
           reflow once the title arrives. */
        title={loading || !data ? <Skeleton height={16} width={220} /> : data.task.title}
        actions={
          data && data.canEdit !== false && (
            <Menu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              items={[
                {
                  id: "save-template",
                  label: "Save as template",
                  icon: "copy",
                  onSelect: () => setTemplateOpen(true),
                },
                {
                  id: "delete",
                  label: "Delete",
                  icon: "trash",
                  destructive: true,
                  onSelect: () => setDeleteOpen(true),
                },
              ]}
            >
              <IconButton
                icon={<Icon name="more" size={16} />}
                label="More actions"
                variant={menuOpen ? "soft" : "ghost"}
                onClick={() => setMenuOpen((v) => !v)}
              />
            </Menu>
          )
        }
      />

      <div className="task-detail__body">
        <section className="task-detail__pane task-detail__pane--main" aria-label="Task">
          {loading || !data ? (
            <div className="task-detail__loading">
              <SkeletonText lines={3} />
              <Skeleton height={80} />
              <SkeletonText lines={4} />
            </div>
          ) : (
            <>
              <fieldset className="task-detail__fieldset" disabled={data.canEdit === false}>
              <EditableTitle value={data.task.title} onSave={(title) => patchTask({ title })} />

              <div className="task-detail__field-row">
                <Select
                  label="Status"
                  value={data.task.status}
                  onChange={(e) => patchTask({ status: e.target.value })}
                >
                  {Object.entries(STATUS_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>

                <Select
                  label="Priority"
                  value={data.task.priority}
                  onChange={(e) => patchTask({ priority: e.target.value })}
                >
                  {["low", "normal", "medium", "high", "urgent"].map((value) => (
                    <option key={value} value={value}>
                      {value[0]!.toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="task-detail__pills">
                <Pill tone={STATUS_TONE[data.task.status] ?? "neutral"}>{STATUS_LABEL[data.task.status] ?? data.task.status}</Pill>
                <Pill tone={PRIORITY_TONE[data.task.priority] ?? "neutral"}>{data.task.priority}</Pill>
              </div>

              <div className="task-detail__section">
                <h3 className="task-detail__section-title">Description</h3>
                <Textarea
                  aria-label="Description"
                  placeholder="Add a description…"
                  defaultValue={data.task.description}
                  maxRows={16}
                  key={data.task.id}
                  onBlur={(e) => {
                    if (e.target.value !== data.task.description) patchTask({ description: e.target.value });
                  }}
                />
              </div>

              <div className="task-detail__field-row">
                <Input
                  type="date"
                  label="Due date"
                  value={toDateInputValue(data.task.dueAt)}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Built as an explicit UTC instant rather than passed
                    // through `new Date(v)`. A bare YYYY-MM-DD happens to
                    // parse as UTC, but the same string with a time appended
                    // parses as local — a distinction that quietly shifts the
                    // date by a day depending on which form is used.
                    patchTask({ dueAt: v ? `${v}T00:00:00.000Z` : null });
                  }}
                />
                {/*
                 * Text, not a number field.
                 *
                 * "Estimate (minutes)" made a week-long task 2400 — a number
                 * nobody types and nobody reads back. This takes what people
                 * say (3d, 4h 30m, 2w) and stores minutes, so the column is
                 * unchanged and every estimate already saved still means what
                 * it meant.
                 */}
                <Input
                  label="Estimate"
                  placeholder="e.g. 3d, 4h 30m, 2w"
                  defaultValue={formatDuration(data.task.estimateMinutes)}
                  key={`estimate-${data.task.id}-${data.task.estimateMinutes ?? "none"}`}
                  onBlur={(e) => {
                    const parsed = parseDuration(e.target.value);
                    if (!parsed.ok) {
                      setEstimateError(parsed.error);
                      return;
                    }
                    setEstimateError(null);
                    // 0 and null both mean "not estimated"; storing 0 would
                    // claim somebody had sized it at nothing.
                    const next = parsed.minutes === 0 ? null : parsed.minutes;
                    if (next !== data.task.estimateMinutes) patchTask({ estimateMinutes: next });
                  }}
                />
              </div>
              {estimateError ? (
                <p className="task-detail__hint task-detail__hint--error" role="alert">
                  {estimateError}
                </p>
              ) : (
                <p className="task-detail__hint">{DURATION_HINT}</p>
              )}

              {data.task.dueAt && (
                <p className="task-detail__hint">Due {formatDueDate(data.task.dueAt)}</p>
              )}

              {/*
               * Repeats.
               *
               * Offered as a handful of ordinary rhythms rather than an RRULE
               * box — the string is a storage format, not something to ask
               * anyone to write. A rule the list does not cover (one made
               * elsewhere, or by hand) is kept and shown as "Custom" rather
               * than being silently rewritten into the nearest option.
               */}
              <div className="task-detail__field-row">
                <Select
                  label="Repeats"
                  value={RECURRENCE_PRESETS.some((p) => p.rule === data.task.recurrenceRule) ? (data.task.recurrenceRule ?? "") : "custom"}
                  disabled={!data.task.dueAt}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "custom") return;
                    patchTask({ recurrenceRule: value || null });
                  }}
                >
                  {RECURRENCE_PRESETS.map((preset) => (
                    <option key={preset.label} value={preset.rule ?? ""}>
                      {preset.label}
                    </option>
                  ))}
                  {data.task.recurrenceRule && !RECURRENCE_PRESETS.some((p) => p.rule === data.task.recurrenceRule) && (
                    <option value="custom">Custom</option>
                  )}
                </Select>
              </div>
              {!data.task.dueAt ? (
                // The rule is counted from the due date, so saying so is more
                // use than a disabled control with no explanation.
                <p className="task-detail__hint">Give this a due date to make it repeat.</p>
              ) : (
                data.task.recurrenceRule && (
                  <p className="task-detail__hint">
                    The next one is created when this is completed — later instances do not exist until then.
                  </p>
                )
              )}

              <div className="task-detail__people">
                <div className="task-detail__person">
                  <span className="task-detail__person-label">Reporter</span>
                  <div className="task-detail__person-chip">
                    {data.task.reporterName && <Avatar name={data.task.reporterName} size={22} />}
                    <span>{data.task.reporterName ?? "Unknown"}</span>
                  </div>
                </div>
                {/*
                 * Assignee is a control, not a label.
                 *
                 * The reporter is history — who raised it, fixed at creation.
                 * The assignee is a decision that gets made and remade, often
                 * after the task exists, so a task created unassigned has to
                 * be assignable without deleting and recreating it.
                 */}
                <div className="task-detail__person task-detail__person--owners">
                  <span className="task-detail__person-label">
                    {data.task.assignees.length > 1 ? "Owners" : "Assignee"}
                  </span>
                  <div className="task-detail__owners">
                    {data.task.assignees.map((owner) => {
                      const isMe = owner.userId === auth.viewer?.id;
                      return (
                        <div
                          key={owner.userId}
                          className={`task-detail__owner ${owner.completedAt ? "is-done" : ""}`}
                        >
                          <Avatar name={owner.displayName} size={22} src={owner.avatarUrl ?? undefined} />
                          <span className="task-detail__owner-name">{owner.displayName}</span>
                          {/*
                           * Only your own box is yours to tick. Marking somebody
                           * else finished would be a claim about their work, so
                           * everyone else's state is shown but not editable.
                           */}
                          {isMe ? (
                            <Checkbox
                              checked={Boolean(owner.completedAt)}
                              onChange={(event) => setMyCompletion(event.target.checked)}
                              label="Done"
                            />
                          ) : (
                            owner.completedAt && (
                              <span className="task-detail__owner-done" title="Finished their part">
                                <Icon name="check" size={13} />
                              </span>
                            )
                          )}
                          <IconButton
                            icon={<Icon name="close" size={13} />}
                            label={`Remove ${owner.displayName}`}
                            size="sm"
                            onClick={() =>
                              setOwners(data.task.assignees.filter((a) => a.userId !== owner.userId).map((a) => a.userId))
                            }
                          />
                        </div>
                      );
                    })}

                    <Select
                      aria-label="Add an owner"
                      value=""
                      onChange={(event) => {
                        const id = event.target.value;
                        if (!id) return;
                        setOwners([...data.task.assignees.map((a) => a.userId), id]);
                      }}
                    >
                      <option value="">
                        {data.task.assignees.length === 0 ? "Unassigned — add someone…" : "Add someone…"}
                      </option>
                      {people
                        .filter((person) => !data.task.assignees.some((a) => a.userId === person.id))
                        .map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.displayName}
                            {person.jobTitle ? ` · ${person.jobTitle}` : ""}
                          </option>
                        ))}
                    </Select>
                  </div>

                  {/*
                   * The quorum rule only means anything once a second person is
                   * on the task — with one owner the two rules are the same
                   * rule, and offering the choice would just be a control that
                   * changes nothing.
                   */}
                  {data.task.assignees.length > 1 && (
                    <Select
                      aria-label="Completion rule"
                      className="task-detail__quorum"
                      value={data.task.completionMode}
                      onChange={(event) => patchTask({ completionMode: event.target.value as CompletionMode })}
                    >
                      <option value="any">Done when anyone finishes</option>
                      <option value="all">Done when everyone finishes</option>
                    </Select>
                  )}
                </div>
              </div>

              <TaskStructureFields
                taskId={data.task.id}
                projectId={data.task.projectId}
                sectionId={data.task.sectionId ?? null}
                milestoneId={data.task.milestoneId ?? null}
                tags={data.task.tags ?? []}
                canEdit={canEditTask}
                onPatch={(fields) => patchTask(fields)}
                onTagsChanged={(tags) => setData((prev) => (prev ? { ...prev, task: { ...prev.task, tags } } : prev))}
              />

              <TaskCustomFields
                taskId={data.task.id}
                projectId={data.task.projectId}
                values={data.task.customValues ?? {}}
                canEdit={canEditTask}
                people={people}
                onChanged={(customValues) => setData((prev) => (prev ? { ...prev, task: { ...prev.task, customValues } } : prev))}
              />

              <TaskApproval
                taskId={data.task.id}
                projectId={data.task.projectId}
                approval={data.approval ?? null}
                canEdit={canEditTask}
                closed={data.task.status === "done" || data.task.status === "cancelled"}
                onChanged={(approval) => setData((prev) => (prev ? { ...prev, approval } : prev))}
              />

              <TaskChecklist
                taskId={data.task.id}
                initial={data.checklist ?? []}
                people={people}
                canEdit={canEditTask}
              />

              <BlockerEditor
                task={data.task}
                blockedBy={data.blockedBy}
                blocking={data.blocking}
                canEdit={canEditTask}
                onAdd={addBlocker}
                onRemove={removeBlocker}
                onOpen={(id) => navigate(`/work/task/${id}`)}
              />

              {!data.task.parentTaskId && (
                <div className="task-detail__section">
                  <div className="task-detail__section-header">
                    <h3 className="task-detail__section-title">Subtasks</h3>
                    {subtaskProgress && (
                      <span className="task-detail__subtask-progress">
                        {subtaskProgress.done} / {subtaskProgress.total}
                      </span>
                    )}
                  </div>

                  <div className="task-detail__subtask-list">
                    {data.subtasks.map((subtask) => (
                      <SubtaskRow
                        key={subtask.id}
                        subtask={subtask}
                        onToggle={toggleSubtask}
                        onOpen={(id) => navigate(`/work/task/${id}`)}
                      />
                    ))}
                  </div>

                  <form
                    className="task-detail__add-subtask"
                    onSubmit={(e) => {
                      e.preventDefault();
                      addSubtask();
                    }}
                  >
                    <Icon name="plus" size={14} />
                    <input
                      value={newSubtaskTitle}
                      onChange={(e) => setNewSubtaskTitle(e.target.value)}
                      placeholder="Add subtask…"
                      aria-label="New subtask title"
                      disabled={addingSubtask}
                    />
                  </form>
                </div>
              )}

              <TaskTime taskId={data.task.id} taskTitle={data.task.title} canLog={canEditTask} />

              {/* Supporting material for the task — briefs, screenshots, specs.
                  The files API has always accepted a taskId scope; this is the
                  first surface to use it. */}
              <div className="task-detail__section">
                <AttachmentList
                  scope={{ taskId: data.task.id }}
                  emptyText="No files attached. Add a brief, a screenshot, or a spec."
                />
              </div>
              </fieldset>
            </>
          )}
        </section>

        <section className="task-detail__pane task-detail__pane--activity" aria-label="Activity">
          <div className="task-detail__activity-header">
            <h2>Activity</h2>
          </div>

          {data && (
            <div className="task-detail__activity-tools">
              <div className="task-detail__filter" role="group" aria-label="Filter activity">
                {(["all", "comments", "history"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`task-detail__filter-btn ${filter === value ? "is-active" : ""}`}
                    aria-pressed={filter === value}
                    onClick={() => setFilter(value)}
                  >
                    {value === "all" ? "All" : value === "comments" ? "Comments" : "History"}
                  </button>
                ))}
              </div>
              <div className="task-detail__followers">
                <div className="task-detail__follower-stack" aria-label={`${data.followers.length} following`}>
                  {data.followers.slice(0, 5).map((f) => (
                    <span key={f.userId} title={f.displayName}>
                      <Avatar name={f.displayName} size={20} src={f.avatarUrl ?? undefined} />
                    </span>
                  ))}
                  {data.followers.length > 5 && <span className="task-detail__follower-more">+{data.followers.length - 5}</span>}
                </div>
                <Button size="sm" variant="secondary" loading={followBusy} onClick={toggleFollow}>
                  {data.isFollowing ? "Unfollow" : "Follow"}
                </Button>
              </div>
            </div>
          )}

          <div className="task-detail__activity-scroll" ref={scrollRef}>
            {loading ? (
              <div className="task-detail__loading">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="task-detail__comment-skeleton">
                    <Skeleton circle width={28} height={28} />
                    <SkeletonText lines={2} />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {filter !== "comments" && historyCursor && (
                  <Button size="sm" variant="ghost" onClick={loadEarlierHistory}>
                    Load earlier history
                  </Button>
                )}
                {thread.length === 0 ? (
                  <EmptyState icon="chat" title="No activity yet" description="Comments and changes on this task will show up here." />
                ) : (
                  thread.map((item) =>
                    item.type === "history" ? (
                      <div key={item.entry.id} className="task-detail__history">
                        <Icon name="clock" size={12} />
                        <span>
                          <strong>{item.entry.actorName ?? "Someone"}</strong> {describeActivity(item.entry)}
                        </span>
                        <time dateTime={item.entry.createdAt}>{formatRelative(item.entry.createdAt)}</time>
                      </div>
                    ) : (
                      <div key={item.comment.id} className={`task-detail__comment ${item.comment.pending ? "is-pending" : ""}`}>
                        <Avatar name={item.comment.authorName} size={28} src={item.comment.authorAvatarUrl ?? undefined} />
                        <div className="task-detail__comment-body">
                          <div className="task-detail__comment-header">
                            <strong>{item.comment.authorName}</strong>
                            <time dateTime={item.comment.createdAt}>{formatRelative(item.comment.createdAt)}</time>
                            {item.comment.editedAt && !item.comment.deleted && <span className="task-detail__edited">edited</span>}
                            {!item.comment.pending && !item.comment.deleted && editingId !== item.comment.id &&
                              (item.comment.authorId === auth.viewer?.id || data?.canModerateComments) && (
                                <span className="task-detail__comment-actions">
                                  {item.comment.authorId === auth.viewer?.id && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingId(item.comment.id);
                                        setEditValue(stripMentions(item.comment.body));
                                      }}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  <button type="button" onClick={() => deleteComment(item.comment.id)}>
                                    Delete
                                  </button>
                                </span>
                              )}
                          </div>
                          {item.comment.deleted ? (
                            <p className="task-detail__comment-deleted">comment deleted</p>
                          ) : editingId === item.comment.id ? (
                            <div className="task-detail__comment-edit">
                              <MentionField
                                value={editValue}
                                onChange={setEditValue}
                                people={people}
                                label="Edit comment"
                                onSubmit={saveCommentEdit}
                                onCancel={() => setEditingId(null)}
                                autoFocus
                              />
                              <div className="task-detail__comment-edit-bar">
                                <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={saveCommentEdit} disabled={!editValue.trim()}>
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p>
                              <CommentText body={item.comment.body} viewerId={auth.viewer?.id} />
                            </p>
                          )}
                        </div>
                      </div>
                    ),
                  )
                )}
              </>
            )}
          </div>

          <CommentComposer onSend={sendComment} people={people} />
        </section>
      </div>

      <SaveTemplateDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        path={`/tasks/${taskId}/save-as-template`}
        defaultName={data?.task.title ?? ""}
        noun="task"
      />

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete task?">
        <p className="task-detail__modal-copy">
          The task moves to the trash with its subtasks, comments and attachments. It can be restored for 30 days,
          then it is removed for good.
        </p>
        <div className="task-detail__modal-actions">
          <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={handleDelete}>
            Delete task
          </Button>
        </div>
      </Modal>
    </div>
  );
}
